import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { getFileItems } from '../../../../api'
import { EMPTY_OBJECT } from '../../../../constants/common'
import { FILE_SCOPE_REQUEST } from '../../../../constants/file'
import { SIZE } from '../../../../constants/size'
import { useUk, useUserData } from '../../../../hooks'
import { isStaffUser } from '../../../../utils/user'
import {
  ATTACH_ACTION_SELECT_ACTION_FILE,
  ATTACH_ACTION_SELECT_FILE_FROM_STORAGE,
  AttachActionSelect,
} from '../../../AttachActionSelect'
import { Field } from '../../../Field'
import {
  FileAttachItemsModal,
  FileList,
  FilePreview,
  useAttachStorageFiles,
  useFileUpload,
} from '../../../File'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../../FilesPreviewGallery'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../Form'
import { useFormikFieldValue } from '../../../Formik'
import { Content } from '../../../Modal'
import { useWysiwygEditorFullScreenHeight } from '../../../WysiwygEditor'
import {
  CHAT_FORM_FIELD_NAME_FILES,
  CHAT_FORM_FIELD_NAME_STORAGE_FILES,
} from '../ChatForm.constants'
import { getChatFormAttachActions } from '../ChatForm.utils'

import { hasFileWithId } from './ChatFormFields.utils'
import { LabelField } from './LabelField'
import { MessageField } from './MessageField'
import { ParticipantField } from './ParticipantField'
import { TitleField } from './TitleField'

import * as styles from './ChatFormFields.module.css'

function ChatFormFields({
  ContentComponent = Content,
  contentComponentProps: {
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  notifications,
  setModal,
  submitButtonText,
  onCancel,
  onRemoveNotification,
  onSetNotification,
}) {
  const { t } = useTranslation()

  const { dirty, isSubmitting } = useFormikContext()

  const { contentRef, contentWrapperRef, handleEditorInit } =
    useWysiwygEditorFullScreenHeight({
      isFullScreen: contentComponentProps?.isFullScreen,
    })

  const [files, setFiles] = useFormikFieldValue(CHAT_FORM_FIELD_NAME_FILES)

  const [storageFiles, setStorageFiles] = useFormikFieldValue(
    CHAT_FORM_FIELD_NAME_STORAGE_FILES
  )

  const user = useUserData()

  const { directory_id: ukDocumentsFolder } = useUk()

  const isStaffUserAcc = isStaffUser(user)

  const {
    filesError,
    filesUploading,
    handleExcludeFile,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onSetFiles: setFiles,
  })

  const { handleAddStorageFile, handleRemoveStorageFile } =
    useAttachStorageFiles({ onSetFiles: setStorageFiles })

  const allFiles = [...storageFiles, ...files]

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({ files: allFiles })

  useFormValidationErrorsNotification({
    files: { filesError, filesUploading },
    onRemoveNotification,
    onSetNotification,
  })

  function handleCloseModal() {
    setModal(null)
  }

  function handleAttachAction(action) {
    switch (action.value) {
      case ATTACH_ACTION_SELECT_ACTION_FILE:
        // handling action in `AttachActionSelect`
        break
      case ATTACH_ACTION_SELECT_FILE_FROM_STORAGE:
        setModal(
          <FileAttachItemsModal
            ButtonRemove={false}
            directoryId={ukDocumentsFolder}
            getData={getFileItems}
            initialValues={storageFiles}
            onClose={handleCloseModal}
            onSubmit={handleAddStorageFile}
          />
        )
        break
      default:
        return null
    }
  }

  function handleDeleteFile(id) {
    if (hasFileWithId(storageFiles, id)) {
      handleRemoveStorageFile(id)
    } else if (hasFileWithId(files, id)) {
      handleExcludeFile(id)
    } else {
      handleRemoveFile(id)
    }
  }

  function renderFile(file, key) {
    return (
      <FilePreview
        disabled={isSubmitting}
        file={file}
        key={key}
        onClick={handleClickFile}
        onRemoveFile={handleDeleteFile}
      />
    )
  }

  function handleCancel() {
    onCancel({
      dirty,
      files: allFiles,
    })
  }

  const attachActionOptions = getChatFormAttachActions({ t })

  const showFiles =
    allFiles?.length > 0 || filesUploading?.length > 0 || filesError?.length > 0

  return (
    <FormBody
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: false,
        UploadComponent: isStaffUserAcc ? AttachActionSelect : undefined,
        cancelProps: {
          onClick: handleCancel,
        },
        className: styles.actions,
        isSubmitting,
        submitProps: {
          isLoading: isSubmitting,
          title: submitButtonText,
        },
        uploadProps: {
          ...(isStaffUserAcc
            ? { options: attachActionOptions, onClick: handleAttachAction }
            : null),
          onFinishUpload: handleUploadFile,
          onStartUpload: handleStartUploading,
          onUploadErrors: handleUploadErrors,
        },
      }}
    >
      <ContentComponent
        {...contentComponentProps}
        contentRef={contentRef}
        contentWrapperRef={contentWrapperRef}
        size={contentSize}
      >
        <FormInlineNotifications notifications={notifications} />
        <FormFieldsContainer className={styles.formFields}>
          <TitleField />
          <ParticipantField />
          {isStaffUserAcc ? <LabelField /> : null}
          <MessageField onInit={handleEditorInit} />
          {showFiles ? (
            <Field
              shouldAdaptToMobileScreen
              disabled={isSubmitting}
              label={`${t('Request.AttachLabelKey')}:`}
            >
              <FileList
                errorItems={filesError}
                files={allFiles}
                renderItem={renderFile}
                uploadingItems={filesUploading}
                onRemoveFile={handleRemoveFile}
              />
            </Field>
          ) : null}
          <FilesPreviewGallery
            files={allFiles}
            initActive={activeFileIndex}
            scope={FILE_SCOPE_REQUEST}
            onClose={handleCloseFilePreview}
          />
        </FormFieldsContainer>
      </ContentComponent>
    </FormBody>
  )
}

export default ChatFormFields
